/**
 * 플러그인 — **모듈을 어느 단계로 어디까지 내보낼지 정하는 개발자 전용 화면**(`plugin:manage`).
 *
 * 한때 이 화면은 설정 두 벌(백업 외부 보관·장애 알림 발송)이었고 '베타' 딱지는 코드에 박혀 있었다.
 * 그래서 기능을 베타에서 내리거나 현장에서 한 기능만 멈추려면 코드를 고쳐 다시 배포해야 했다.
 * 지금은 그것이 이 화면의 본업이다 — 설정은 그 아래에 있다.
 *
 * ## 모듈마다 네 가지를 정한다 (넷이 서로 독립이다)
 *
 *   사용 · 해제      도는가. 해제하면 예약·발송·주기 점검이 멈추고 **설정은 남는다**.
 *   베타 · 정식      단계. 콘솔 곳곳의 '베타' 딱지와 경고문이 이 값을 본다.
 *   고객사 공개      배포 ①. 관리자(고객사)도 이 모듈의 상태를 본다. 비밀과 변경은 그대로 공급사 것이다.
 *   패키지 기본값    배포 ②. 새로 설치한 서버가 이 상태로 시작한다(`MODULE_DEFAULTS` 한 줄로 실려 나간다).
 *
 * 값은 세 겹으로 정해진다 — 제품 기본값(코드) ← 패키지 기본값(`MODULE_DEFAULTS`) ← 이 서버의 결정(여기).
 * 모듈마다 출처를 적어 두는 이유는 "왜 이 현장만 다른가" 를 늘 설명할 수 있어야 하기 때문이다.
 *
 * ## 개발자 전용이다
 *
 * 고객사 관리자(`admin`)는 이 주소를 열지 못한다(서버가 `plugin:manage` 를 요구한다). 여기서 누르는 것은
 * 고객사 화면과 서버 동작에 곧바로 반영되고, 모두 감사로그에 남는다.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FolderCheck, Play, Save, Send, ToggleLeft, ToggleRight } from 'lucide-react';
import type {
  AlertChannel,
  AlertSettingsInput,
  AlertSettingsView,
  BackupExternalSettings,
  ModuleFlags,
  ModuleId,
  ModuleView,
  PathInspection,
  PluginRunView,
  PluginView,
  SignageOneClient,
} from '../api/client.js';
import { Badge, CommandBox, ErrorNote, Loading, Panel, formatBytes, relativeTime } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { Disclosure } from '../components/Disclosure.js';
import { focusFirstInvalid, numberProblem, parseNumberText, type NumberRule } from '../components/form/NumberInput.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { confirmDialog, type ConfirmOptions } from '../components/ConfirmDialog.js';

const CHANNEL_LABEL: Readonly<Record<AlertChannel, string>> = { email: '메일', messenger: '메신저', sms: 'SMS' };

const PLUGIN_LABEL: Readonly<Record<PluginRunView['plugin'], string>> = {
  'backup-external': '백업 외부 보관',
  'alert-notify': '장애 알림 발송',
};

const ACTION_LABEL: Readonly<Record<PluginRunView['action'], string>> = { run: '실행', check: '경로 점검', test: '시험 발송' };

/**
 * 이 모듈의 설정은 어느 화면에 있나 — **콘솔만 아는 것**이라 서버의 모듈 표에 넣지 않았다.
 * 설정 화면이 없는 모듈(장애 감시)도 어디를 봐야 하는지는 말해 준다.
 */
const WHERE_SETTINGS: Readonly<Record<ModuleId, string>> = {
  'backup-external': '설정은 아래 [백업 외부 보관] 에 있습니다.',
  'alert-notify': '설정은 아래 [장애 알림 발송] 에 있습니다.',
  'alert-monitor': '주기·문턱은 서버 설정(MONITOR_INTERVAL_SEC·ALERT_*), 기록은 정비 모드 ▸ 장애 감시에 있습니다.',
  hub: '서버 등록과 현황은 통합 관제(모니터링 아래 메뉴)에 있습니다. 사용으로 바꾸면 메뉴가 나타납니다.',
};

/** 이만큼 지나도 `running` 이면 서버가 도중에 멈춘 것이다 — 끝을 모른다. */
const STALE_RUN_MS = 6 * 3_600_000;

export function PluginsScreen({ client }: { client: SignageOneClient }): ReactNode {
  const list = useAsync(() => client.listPlugins(), [client]);
  const runs = list.data?.runs ?? [];
  const running = runs.some((run) => run.status === 'running' && !isStale(run));
  // 실행 중일 때만 자주 본다 — 끝나면 결과가 저절로 바뀌어 보이게.
  usePolling(list.reload, 3_000, running);

  const modules = list.data?.modules ?? [];
  const moduleOf = (id: ModuleId): ModuleView | undefined => modules.find((module) => module.id === id);
  const backup = list.data?.plugins.find((plugin): plugin is Extract<PluginView, { id: 'backup-external' }> => plugin.id === 'backup-external');
  const notify = list.data?.plugins.find((plugin): plugin is Extract<PluginView, { id: 'alert-notify' }> => plugin.id === 'alert-notify');
  const backupModule = moduleOf('backup-external');
  const notifyModule = moduleOf('alert-notify');

  if (!list.data) {
    return <div className="screen">{list.error ? <ErrorNote message={list.error} /> : <Loading />}</div>;
  }

  return (
    <div className="screen">
      {list.error ? <ErrorNote message={list.error} /> : null}
      <ModulesPanel client={client} modules={modules} onChanged={list.reload} />
      <PackagedPanel defaults={list.data.module_defaults} />
      {backup && backupModule ? (
        <BackupExternalPanel
          client={client}
          module={backupModule}
          settings={backup.settings}
          suggestedPath={backup.environment.suggested_path}
          inContainer={backup.environment.in_container}
          running={runs.some((run) => run.plugin === 'backup-external' && run.action === 'run' && run.status === 'running' && !isStale(run))}
          onChanged={list.reload}
        />
      ) : null}
      {notify && notifyModule ? <AlertNotifyPanel client={client} module={notifyModule} view={notify.settings} onChanged={list.reload} /> : null}
      <RunLogPanel runs={runs} />
    </div>
  );
}

function isStale(run: PluginRunView, now = Date.now()): boolean {
  return run.status === 'running' && now - new Date(run.started_at).getTime() > STALE_RUN_MS;
}

function when(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return `${date.toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} (${relativeTime(iso)})`;
}

/* ─────────────────────────────── 모듈 ─────────────────────────────── */

/** 이 값이 어디서 왔나 — 한 줄로. 출처를 감추면 "왜 이 현장만 다른가" 를 아무도 설명하지 못한다. */
export function moduleSourceHint(module: ModuleView): string {
  if (module.source === 'server') return '이 서버에서 정한 값입니다.';
  if (module.source === 'package') return '패키지 기본값을 따릅니다.';
  return '제품 기본값을 따릅니다.';
}

const FLAG_WORD: Readonly<Record<'enabled' | 'beta' | 'exposed', (on: boolean) => string>> = {
  enabled: (on) => (on ? '사용' : '해제'),
  beta: (on) => (on ? '베타' : '정식'),
  exposed: (on) => (on ? '고객사 공개' : '공급사 전용'),
};

/**
 * 패키지가 정한 것과 지금 이 서버가 다른 점. 같으면 null.
 *
 * 다음 판을 새로 설치하면 패키지 쪽으로 돌아간다 — 이 서버에서 급히 끈 것을 패키지에도 반영했는지
 * 여기서 눈으로 확인한다.
 */
export function packageDiff(module: ModuleView): string | null {
  const wanted = module.package_default;
  if (!wanted) return null;
  const differs = (['enabled', 'beta', 'exposed'] as const).filter((key) => wanted[key] !== undefined && wanted[key] !== module[key]);
  if (differs.length === 0) return null;
  return `패키지 기본값은 ${differs.map((key) => FLAG_WORD[key](wanted[key] as boolean)).join(' · ')} 입니다.`;
}

/** 모듈 스위치 하나 — **색과 말을 함께** 쓴다. 색만으로 상태를 말하면 무엇이 켜진 것인지 모른다. */
function Switch({
  on,
  onLabel,
  offLabel,
  title,
  disabled,
  onToggle,
}: {
  on: boolean;
  onLabel: string;
  offLabel: string;
  title: string;
  disabled: boolean;
  onToggle: () => void;
}): ReactNode {
  const Icon = on ? ToggleRight : ToggleLeft;
  return (
    <button
      type="button"
      className={`btn btn-tiny ${on ? 'btn-primary' : ''}`}
      aria-pressed={on}
      title={title}
      disabled={disabled}
      onClick={onToggle}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {on ? onLabel : offLabel}
    </button>
  );
}

function ModulesPanel({
  client,
  modules,
  onChanged,
}: {
  client: SignageOneClient;
  modules: readonly ModuleView[];
  onChanged: () => void;
}): ReactNode {
  const action = useAction();
  const [notice, setNotice] = useState<string | null>(null);

  const set = async (module: ModuleView, patch: Partial<ModuleFlags>, confirm?: ConfirmOptions): Promise<void> => {
    if (confirm && !(await confirmDialog(confirm))) return;
    void action.run(
      () => client.setModule(module.id, patch),
      (next) => {
        setNotice(`${next.name} — ${describe(next)}`);
        onChanged();
      },
    );
  };

  return (
    <Panel
      title="모듈"
      help="변경은 즉시 제품 동작에 반영되고 감사로그에 남습니다. 값은 제품 기본값 ← 패키지 기본값 ← 이 서버의 결정 순으로 덮어씁니다. 해제: 동작만 멈추고 설정은 유지(다시 켜면 같은 설정으로 이어짐) · 고객사 공개: 관리자에게 상태만 표시(비밀과 설정 변경은 공급사만) · 패키지 포함: 새 설치의 시작 상태."
      actions={<Badge tone="danger">개발자 전용</Badge>}
    >
      <div>
        {action.error || notice ? (
          <div className="space-y-1.5 border-b border-slate-200 p-4">
            {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
            {notice ? <p className="hint">{notice}</p> : null}
          </div>
        ) : null}
        {modules.map((module) => (
          <div key={module.id} className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 p-4 last:border-b-0">
            <div className="min-w-[17rem] flex-1 space-y-1">
              <p className="flex flex-wrap items-center gap-1.5 text-[13.5px] font-semibold">
                {module.name}
                <Badge tone={module.beta ? 'warn' : 'ok'}>{module.beta ? '베타' : '정식'}</Badge>
                {module.enabled ? null : <Badge tone="danger">해제됨</Badge>}
                {module.exposed ? <Badge tone="info">고객사 공개</Badge> : null}
                {module.packaged ? <Badge tone="brand">패키지 기본값</Badge> : null}
                <code className="mono text-[11.5px] font-normal text-slate-500">{module.id}</code>
              </p>
              <p className="hint">{module.summary}</p>
              {module.enabled ? null : <p className="text-[12.5px] text-red-700">{module.stops}</p>}
              {module.last_run ? (
                <p className="hint">
                  마지막 {ACTION_LABEL[module.last_run.action]} {when(module.last_run.started_at)} ·{' '}
                  {module.last_run.status === 'ok' ? '성공' : module.last_run.status === 'failed' ? '실패' : '실행 중'} · {module.last_run.message}
                </p>
              ) : null}
              {/*
                단계·공개·패키지 스위치는 출시를 정할 때만 만진다 — 사용/해제만 밖에 두고 [배포 설정] 뒤로(점진적 공개).
                지금 값은 이름 옆 배지와 접힌 머리에 남는다.
              */}
              <Disclosure className="mt-2" title="배포 설정" summary={releaseSummary(module)}>
                <div className="space-y-2">
                  <p className="hint">
                    {moduleSourceHint(module)} {packageDiff(module) ?? ''} {WHERE_SETTINGS[module.id]}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                  <Switch
                    on={module.beta}
                    onLabel="베타"
                    offLabel="정식"
                    title="콘솔의 '베타' 표시와 경고문에 반영됩니다"
                    disabled={action.pending}
                    onToggle={() => set(module, { beta: !module.beta })}
                  />
                  <Switch
                    on={module.exposed}
                    onLabel="고객사 공개"
                    offLabel="공급사 전용"
                    title="관리자(고객사)에게 이 모듈의 상태를 보입니다"
                    disabled={action.pending}
                    onToggle={() =>
                      set(
                        module,
                        { exposed: !module.exposed },
                        module.exposed
                          ? undefined
                          : {
                              title: `'${module.name}' 을 고객사에 공개할까요?`,
                              body: '관리자 화면(시스템·장애 감시)에 이름과 상태가 표시됩니다.',
                              confirmLabel: '공개',
                            },
                      )
                    }
                  />
                  <Switch
                    on={module.packaged}
                    onLabel="패키지 포함"
                    offLabel="패키지 제외"
                    title="새로 설치하는 서버가 이 상태로 시작합니다(아래 패키지 기본값 줄에 들어갑니다)"
                    disabled={action.pending}
                    onToggle={() => set(module, { packaged: !module.packaged })}
                  />
                  </div>
                </div>
              </Disclosure>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Switch
                on={module.enabled}
                onLabel="사용"
                offLabel="해제"
                title={module.enabled ? `해제하면: ${module.stops}` : '다시 켭니다'}
                disabled={action.pending}
                onToggle={() =>
                  set(
                    module,
                    { enabled: !module.enabled },
                    module.enabled
                      ? {
                          title: `'${module.name}' 을 해제할까요?`,
                          body: `${module.stops}\n설정은 유지됩니다.`,
                          confirmLabel: '해제',
                          danger: true,
                        }
                      : undefined,
                  )
                }
              />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/** [배포 설정] 이 접혀 있을 때의 한 줄 — 단계·공개·패키지. */
export function releaseSummary(module: Pick<ModuleView, 'beta' | 'exposed' | 'packaged'>): string {
  return [FLAG_WORD.beta(module.beta), FLAG_WORD.exposed(module.exposed), module.packaged ? '패키지 포함' : '패키지 제외'].join(' · ');
}

/** 바뀐 뒤의 상태를 한 줄로 — 누른 사람이 결과를 말로 확인한다. */
function describe(module: ModuleView): string {
  return [
    FLAG_WORD.enabled(module.enabled),
    FLAG_WORD.beta(module.beta),
    FLAG_WORD.exposed(module.exposed),
    module.packaged ? '패키지 포함' : '패키지 제외',
  ].join(' · ');
}

/**
 * 패키지 기본값 — **콘솔이 만들고 사람이 옮긴다.**
 *
 * 패키지는 커밋 하나에서 만들어져(`scripts/package-server.mjs`) 인터넷 없는 서버에 설치된다 — 이 서버의
 * DB 가 그 패키지에 닿을 길이 없다. 그래서 정한 것을 **한 줄**로 내보내고, 그 줄을 패키지의
 * `env.template`(또는 이미 나간 서버의 `signageone.env`)에 붙인다. 붙인 줄을 이 화면이 다시 읽어
 * "이 서버가 받은 줄" 로 보여 주므로, 옮겼는지 안 옮겼는지가 눈에 보인다.
 */
function PackagedPanel({ defaults }: { defaults: { line: string; env: string; unknown: string[] } }): ReactNode {
  const empty = defaults.line === 'MODULE_DEFAULTS=';
  return (
    <Panel
      title="패키지 기본값"
      subtitle={empty ? '정한 모듈 없음' : defaults.line.replace('MODULE_DEFAULTS=', '')}
      collapsible
      help="새로 설치하는 서버가 시작할 모듈 상태입니다. 위에서 [패키지 포함]으로 정한 모듈만 실립니다. 이미 설치된 서버는 그 서버의 signageone.env 에 붙이고 재시작합니다."
    >
      <div className="space-y-3 p-4">
        <CommandBox command={defaults.line} />
        <p className="hint">
          <code className="mono">deploy/package/docker/env.template</code> ·{' '}
          <code className="mono">deploy/package/native/env.template</code> 에 넣어 커밋하면 다음 판부터 적용됩니다.
          {empty ? ' 정한 모듈이 없어 빈 줄입니다. 새 설치는 제품 기본값으로 시작합니다.' : ''}
        </p>
        <p className="hint">
          이 서버가 받은 줄: <code className="mono">{defaults.env || '(없음 · 제품 기본값으로 시작)'}</code>
        </p>
        {defaults.unknown.length > 0 ? (
          <p className="text-[12.5px] text-amber-700">
            이 판에 없는 모듈의 설정을 무시했습니다: <code className="mono">{defaults.unknown.join(', ')}</code>
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

/* ──────────────────────── 백업 외부 보관 설정 ──────────────────────── */

interface BackupDraft {
  enabled: boolean;
  path: string;
  keep: string;
  hour: number;
  include_media: boolean;
}

function backupDraftFrom(settings: BackupExternalSettings, suggestedPath: string): BackupDraft {
  return {
    enabled: settings.enabled,
    path: settings.path || suggestedPath,
    keep: String(settings.keep),
    hour: settings.hour,
    include_media: settings.include_media,
  };
}

/** 남길 벌수 칸 — 서버와 같은 범위. 비우거나 벗어나면 저장할 때 짚는다(예전에는 조용히 7·끝값으로 바꿔 저장했다). */
const KEEP_RULE: NumberRule = { label: '남길 벌수', min: 1, max: 365, unit: '벌' };

/** 저장할 것. 벌수는 1~365 로 자른다 — 서버도 같은 범위를 요구한다(저장 전에 `KEEP_RULE` 로 막는다). */
export function backupInput(draft: BackupDraft): Omit<BackupExternalSettings, 'last_scheduled_on'> {
  const keep = Math.min(365, Math.max(1, Math.round(Number(draft.keep)) || 7));
  return { enabled: draft.enabled, path: draft.path.trim(), keep, hour: draft.hour, include_media: draft.include_media };
}

/** 모듈 상태를 설정 패널 머리에. 해제된 모듈의 설정은 **저장은 되고 동작은 하지 않는다.** */
function ModuleBadges({ module }: { module: ModuleView }): ReactNode {
  return (
    <span className="flex items-center gap-1.5">
      <Badge tone={module.beta ? 'warn' : 'ok'}>{module.beta ? '베타' : '정식'}</Badge>
      {module.enabled ? null : <Badge tone="danger">해제됨</Badge>}
      {module.exposed ? <Badge tone="info">고객사 공개</Badge> : null}
    </span>
  );
}

/**
 * 백업 외부 보관.
 *
 * **가장 흔한 사고는 "외부" 가 외부가 아닌 것이다** — 컨테이너 안에 쓰거나, 풀린 NAS 마운트 자리의
 * 빈 폴더에 쓰는 것. 그래서 [경로 점검] 이 그 경로가 어느 마운트 위에 있는지, 여유가 얼마인지, 이미
 * 무엇이 있는지 보여 준다. 서버는 점검을 통과하지 못한 경로로는 켜 주지 않는다.
 */
function BackupExternalPanel({
  client,
  module,
  settings,
  suggestedPath,
  inContainer,
  running,
  onChanged,
}: {
  client: SignageOneClient;
  module: ModuleView;
  settings: BackupExternalSettings;
  suggestedPath: string;
  inContainer: boolean;
  running: boolean;
  onChanged: () => void;
}): ReactNode {
  const saved = useMemo(() => backupDraftFrom(settings, suggestedPath), [settings, suggestedPath]);
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState<BackupDraft>(saved);
  const [inspection, setInspection] = useState<PathInspection | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** [저장] 을 눌렀는데 남길 벌수가 비었거나 범위 밖이었다 — 그 칸을 짚는다(고치면 바로 풀린다). */
  const [showErrors, setShowErrors] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const action = useAction();
  const keepError = numberProblem(parseNumberText(draft.keep), KEEP_RULE);

  useEffect(() => {
    setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 서버 값이 실제로 바뀔 때만 다시 채운다(주기 조회로 적던 칸이 되돌아가지 않게)
  }, [savedKey]);

  // 칸을 비우거나 벗어난 값을 적은 것도 고친 것이다 — [저장] 이 켜져야 눌러서 무엇이 잘못인지 듣는다.
  const dirty =
    JSON.stringify(backupInput(draft)) !== JSON.stringify(backupInput(saved)) ||
    draft.path.trim() !== settings.path ||
    draft.keep.trim() !== saved.keep;
  const set = (patch: Partial<BackupDraft>): void => setDraft((prev) => ({ ...prev, ...patch }));

  const check = (): void =>
    void action.run(
      () => client.checkBackupExternal(draft.path.trim()),
      (result) => {
        setInspection(result);
        setNotice(null);
        onChanged();
      },
    );

  const save = (): void => {
    if (keepError) {
      setShowErrors(true);
      focusFirstInvalid(panelRef.current);
      return;
    }
    setShowErrors(false);
    void action.run(
      () => client.saveBackupExternal(backupInput(draft)),
      () => {
        setNotice('저장했습니다.');
        onChanged();
      },
    );
  };

  const run = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: '지금 백업 파일을 만들까요?',
      body: `${settings.path} 에 만듭니다. 콘텐츠 원본을 포함하면 몇 분 걸립니다.`,
      confirmLabel: '백업 실행',
    });
    if (!ok) return;
    void action.run(
      () => client.runBackupExternal(),
      () => {
        setNotice('실행을 시작했습니다. 결과는 아래 실행 기록에서 확인하세요.');
        onChanged();
      },
    );
  };

  return (
    <Panel
      title="백업 외부 보관"
      help="매일 정한 시각에 백업 파일을 만들어 외부 경로(NAS·다른 디스크)에 둡니다. 형식은 시스템 ▸ 백업과 같고, 복원은 scripts/restore-backup.mjs 로 합니다."
      actions={
        <span className="flex items-center gap-1.5">
          <Badge tone={settings.enabled ? 'ok' : 'neutral'}>{settings.enabled ? `매일 ${String(settings.hour).padStart(2, '0')}시` : '꺼짐'}</Badge>
          <ModuleBadges module={module} />
        </span>
      }
    >
      <div className="space-y-4 p-4" ref={panelRef}>
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {notice ? <p className="hint">{notice}</p> : null}
        {module.enabled ? null : (
          <p className="text-[12.5px] text-red-700">모듈이 해제되어 있습니다. {module.stops}</p>
        )}
        {inContainer ? (
          <p className="hint">
            서버가 Docker 안에서 실행 중입니다. <code className="mono">/backup-external</code> 아래만 쓸 수 있습니다.
          </p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="field sm:col-span-2">
            <span>
              외부 경로
              <HelpTip
                text={
                  inContainer
                    ? '서버가 보는 절대 경로입니다. 실제 저장 위치는 서버 설정의 BACKUP_EXTERNAL_HOST_DIR 로 정하고 서버를 재시작합니다(비우면 같은 디스크).'
                    : '서버가 보는 절대 경로입니다. NAS 는 서버에 먼저 마운트하고, 서버 프로세스가 쓸 수 있게 하세요.'
                }
                label="외부 경로 도움말"
              />
            </span>
            <input
              className="input mono"
              value={draft.path}
              placeholder={suggestedPath || '/mnt/nas/signageone'}
              onChange={(event) => set({ path: event.target.value })}
            />
          </label>
          <label className="field">
            <span>매일 실행 시각 (한국 시각)</span>
            <select className="input" value={draft.hour} onChange={(event) => set({ hour: Number(event.target.value) })}>
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>
                  {String(hour).padStart(2, '0')}시
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>
              남길 벌수
              <HelpTip text="1~365. 넘으면 오래된 백업부터 지웁니다." label="남길 벌수 도움말" />
            </span>
            <input
              className="input"
              inputMode="numeric"
              value={draft.keep}
              aria-invalid={(showErrors && keepError !== null) || undefined}
              onChange={(event) => set({ keep: event.target.value })}
            />
            {showErrors && keepError ? (
              <p className="field-error" role="alert">
                {keepError}
              </p>
            ) : null}
          </label>
        </div>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={draft.include_media} onChange={(event) => set({ include_media: event.target.checked })} />
            콘텐츠 원본(이미지·영상) 포함
            <HelpTip text="빼면 작고 빠르지만, 복원할 때 원본을 따로 옮겨야 합니다." label="콘텐츠 원본 도움말" />
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={draft.enabled} onChange={(event) => set({ enabled: event.target.checked })} />
            매일 자동 실행
            <HelpTip text="경로 점검을 통과한 경로에서만 켜집니다." label="매일 자동 실행 도움말" />
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn" disabled={action.pending || !draft.path.trim()} onClick={check}>
            <FolderCheck className="h-3.5 w-3.5" aria-hidden="true" /> 경로 점검
          </button>
          <button type="button" className="btn btn-primary" disabled={action.pending || !dirty} onClick={save}>
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
          <button
            type="button"
            className="btn"
            disabled={action.pending || running || !settings.path || dirty || !module.enabled}
            title={!module.enabled ? '해제된 모듈입니다' : dirty ? '먼저 저장하세요' : running ? '실행 중입니다' : undefined}
            onClick={run}
          >
            <Play className="h-3.5 w-3.5" aria-hidden="true" /> {running ? '실행 중…' : '지금 실행'}
          </button>
        </div>

        {inspection ? <InspectionResult inspection={inspection} /> : null}
      </div>
    </Panel>
  );
}

function InspectionResult({ inspection }: { inspection: PathInspection }): ReactNode {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[13px]">
      <p className="mb-1.5 flex flex-wrap items-center gap-2">
        <Badge tone={inspection.ok ? 'ok' : 'danger'}>{inspection.ok ? '쓸 수 있음' : '쓸 수 없음'}</Badge>
        <code className="mono">{inspection.path}</code>
      </p>
      {inspection.problems.length > 0 ? (
        <ul className="mb-1.5 list-disc pl-5 text-red-700">
          {inspection.problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
      <p className="hint">
        마운트 지점 <code className="mono">{inspection.mount_point ?? '알 수 없음'}</code> · 여유 {formatBytes(inspection.free_bytes)}
        {inspection.in_container ? ' · 컨테이너 안' : ''}
      </p>
      <p className="hint">
        마운트 지점이 의도한 NAS·디스크인지 확인하세요.
        <HelpTip text="마운트가 풀리면 같은 경로의 빈 폴더에 백업이 조용히 쌓입니다." label="마운트 지점 도움말" />
      </p>
      {inspection.files.length > 0 ? (
        <div className="mt-2">
          <p className="hint mb-0.5">이미 있는 백업 {inspection.files.length}개 (새것부터)</p>
          <ul className="mono text-[12px] text-slate-600">
            {inspection.files.slice(0, 5).map((file) => (
              <li key={file.name}>
                {file.name} · {formatBytes(file.size)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function RunLogPanel({ runs }: { runs: PluginRunView[] }): ReactNode {
  return (
    /* 실행 기록은 찾아볼 때만 — 접어 두되, 실패가 있으면 머리에 적는다(모듈마다 마지막 실행은 위 줄에 있다). */
    <Panel
      title="실행 기록"
      subtitle={(() => {
        const failed = runs.filter((run) => run.status === 'failed').length;
        return failed > 0 ? `최근 30건 · 실패 ${failed}건` : '최근 30건';
      })()}
      help="지금 실행·예약 실행·경로 점검·시험 발송 기록입니다. 90일이 지나면 지워집니다."
      collapsible
    >
      {runs.length === 0 ? (
        <p className="hint p-4">실행 기록이 없습니다.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>시각</th>
                <th>플러그인</th>
                <th>동작</th>
                <th>실행자</th>
                <th>결과</th>
                <th>내용</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} style={{ cursor: 'default' }}>
                  <td className="cell-muted">{when(run.started_at)}</td>
                  <td>{PLUGIN_LABEL[run.plugin]}</td>
                  <td>{ACTION_LABEL[run.action]}</td>
                  <td className="cell-muted">{run.trigger === 'schedule' ? '예약' : run.actor || '—'}</td>
                  <td>
                    <RunStatus run={run} />
                  </td>
                  <td>{run.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function RunStatus({ run }: { run: PluginRunView }): ReactNode {
  if (isStale(run)) return <Badge tone="warn">결과 불명</Badge>;
  if (run.status === 'running') return <Badge tone="info">실행 중</Badge>;
  return <Badge tone={run.status === 'ok' ? 'ok' : 'danger'}>{run.status === 'ok' ? '성공' : '실패'}</Badge>;
}

/* ──────────────────────── 장애 알림 발송 설정 ──────────────────────── */

/** 쉼표·줄바꿈으로 나눈 받는 사람 목록. */
function splitList(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

interface Draft {
  email: { enabled: boolean; host: string; port: string; secure: boolean; user: string; password: string; from: string; to: string };
  messenger: { enabled: boolean; format: AlertSettingsView['messenger']['format']; webhook_url: string };
  sms: {
    enabled: boolean;
    url: string;
    method: 'POST' | 'GET';
    headers: string;
    content_type: 'json' | 'form';
    body_template: string;
    to: string;
  };
}

function draftFrom(view: AlertSettingsView): Draft {
  return {
    email: { ...view.email, port: String(view.email.port), password: '', to: view.email.to.join(', ') },
    messenger: { enabled: view.messenger.enabled, format: view.messenger.format, webhook_url: '' },
    sms: {
      enabled: view.sms.enabled,
      url: '',
      method: view.sms.method,
      headers: '',
      content_type: view.sms.content_type,
      body_template: view.sms.body_template,
      to: view.sms.to.join(', '),
    },
  };
}

/** 저장할 것 — 비밀 칸은 새로 적은 것만 보낸다(빈칸이면 서버가 그대로 둔다). */
/** 메일 포트 칸 — 메일을 켰을 때만 보인다. 비우면 예전에는 조용히 587 로 저장했다. */
const PORT_RULE: NumberRule = { label: '포트', min: 1, max: 65_535 };

export function settingsInput(draft: Draft): AlertSettingsInput {
  return {
    email: {
      enabled: draft.email.enabled,
      host: draft.email.host.trim(),
      port: Number(draft.email.port) || 587,
      secure: draft.email.secure,
      user: draft.email.user.trim(),
      from: draft.email.from.trim(),
      to: splitList(draft.email.to),
      ...(draft.email.password ? { password: draft.email.password } : {}),
    },
    messenger: {
      enabled: draft.messenger.enabled,
      format: draft.messenger.format,
      ...(draft.messenger.webhook_url.trim() ? { webhook_url: draft.messenger.webhook_url.trim() } : {}),
    },
    sms: {
      enabled: draft.sms.enabled,
      method: draft.sms.method,
      content_type: draft.sms.content_type,
      body_template: draft.sms.body_template,
      to: splitList(draft.sms.to),
      ...(draft.sms.url.trim() ? { url: draft.sms.url.trim() } : {}),
      ...(draft.sms.headers.trim() ? { headers: draft.sms.headers.trim() } : {}),
    },
  };
}

/**
 * 장애 알림 발송 — 메일·메신저·SMS 채널.
 *
 * 목록을 주기적으로 다시 받아도 **적던 칸이 되돌아가지 않게** 저장 시각이 바뀔 때만 초안을 새로 만든다.
 */
function AlertNotifyPanel({
  client,
  module,
  view,
  onChanged,
}: {
  client: SignageOneClient;
  module: ModuleView;
  view: AlertSettingsView;
  onChanged: () => void;
}): ReactNode {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(view));
  const [notice, setNotice] = useState<string | null>(null);
  /** [저장] 을 눌렀는데 메일 포트가 비었거나 범위 밖이었다 — 그 칸을 짚는다(고치면 바로 풀린다). */
  const [showErrors, setShowErrors] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const action = useAction();
  const portError = draft.email.enabled ? numberProblem(parseNumberText(draft.email.port), PORT_RULE) : null;

  useEffect(() => {
    setDraft(draftFrom(view));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 저장했을 때만 다시 채운다
  }, [view.updated_at]);

  const set = <K extends keyof Draft>(kind: K, patch: Partial<Draft[K]>): void =>
    setDraft((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));

  const save = (): void => {
    if (portError) {
      setShowErrors(true);
      focusFirstInvalid(panelRef.current);
      return;
    }
    setShowErrors(false);
    void action.run(
      () => client.saveAlertNotify(settingsInput(draft)),
      () => {
        setNotice('저장했습니다.');
        onChanged();
      },
    );
  };

  const test = (channel: AlertChannel): void =>
    void action.run(
      () => client.testAlertChannel(channel),
      (result) => {
        setNotice(
          result.outcome?.ok
            ? `${CHANNEL_LABEL[channel]} 시험 발송을 보냈습니다. 수신 여부를 확인하세요.`
            : `${CHANNEL_LABEL[channel]} 시험 발송 실패: ${result.outcome?.error ?? '알 수 없는 오류'}`,
        );
        onChanged();
      },
    );

  const secretHint = (isSet: boolean): string => (isSet ? '설정됨 · 바꿀 때만 입력' : '');
  const testDisabled = action.pending || !module.enabled;
  const testTitle = module.enabled ? undefined : '해제된 모듈입니다';

  return (
    <Panel
      title="장애 알림 발송"
      help="장애가 생기고 풀릴 때 켜 둔 채널로 알립니다. 발송이 실패해도 장애 기록은 남고 실패 이유가 함께 기록됩니다. 저장한 비밀번호·주소·키는 다시 표시하지 않습니다."
      actions={<ModuleBadges module={module} />}
    >
      <div className="space-y-5 p-4" ref={panelRef}>
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {notice ? <p className="hint">{notice}</p> : null}
        {module.enabled ? null : (
          <p className="text-[12.5px] text-red-700">모듈이 해제되어 있습니다. {module.stops}</p>
        )}
        {module.beta ? (
          <p className="hint">
            베타 기능입니다. 켜기 전에 채널마다 [시험 발송]으로 확인하세요.
            <HelpTip
              text="현장의 메일 서버·메신저·SMS 업체와 오래 운영해 보지 않은 기능입니다."
              label="베타 기능 도움말"
            />
          </p>
        ) : null}

        {/*
          채널마다 칸이 여럿이라 켠 채널의 칸만 낸다(점진적 공개) — 끈 채널의 값은 그대로 남고, 다시 켜면 보인다.
        */}
        <fieldset className="space-y-2">
          <legend className="flex items-center gap-2 text-[13.5px] font-semibold">
            <input type="checkbox" checked={draft.email.enabled} onChange={(event) => set('email', { enabled: event.target.checked })} />
            메일 (SMTP)
          </legend>
          {draft.email.enabled ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="field">
                <span>SMTP 서버</span>
                <input className="input" value={draft.email.host} placeholder="smtp.example.com" onChange={(event) => set('email', { host: event.target.value })} />
              </label>
              <label className="field">
                <span>포트</span>
                <input
                  className="input"
                  value={draft.email.port}
                  inputMode="numeric"
                  aria-invalid={(showErrors && portError !== null) || undefined}
                  onChange={(event) => set('email', { port: event.target.value })}
                />
                {showErrors && portError ? (
                  <p className="field-error" role="alert">
                    {portError}
                  </p>
                ) : null}
              </label>
              <label className="field">
                <span>계정</span>
                <input className="input" value={draft.email.user} autoComplete="off" onChange={(event) => set('email', { user: event.target.value })} />
              </label>
              <label className="field">
                <span>비밀번호</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={draft.email.password}
                  placeholder={secretHint(view.email.password_set)}
                  onChange={(event) => set('email', { password: event.target.value })}
                />
              </label>
              <label className="field">
                <span>보내는 주소</span>
                <input className="input" value={draft.email.from} placeholder="signage@example.com" onChange={(event) => set('email', { from: event.target.value })} />
              </label>
              <label className="field">
                <span>받는 사람 (쉼표로 구분)</span>
                <input className="input" value={draft.email.to} placeholder="ops@example.com" onChange={(event) => set('email', { to: event.target.value })} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={draft.email.secure} onChange={(event) => set('email', { secure: event.target.checked })} />
              TLS 직접 연결 (465 포트)
              <HelpTip text="465 포트에서 켭니다. 587 포트는 끈 채로 두면 STARTTLS 로 암호화합니다." label="TLS 직접 연결 도움말" />
            </label>
            <button type="button" className="btn btn-tiny" disabled={testDisabled} title={testTitle} onClick={() => test('email')}>
              <Send className="h-3 w-3" aria-hidden="true" /> 시험 발송
            </button>
          </>
          ) : null}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="flex items-center gap-2 text-[13.5px] font-semibold">
            <input type="checkbox" checked={draft.messenger.enabled} onChange={(event) => set('messenger', { enabled: event.target.checked })} />
            메신저 (웹훅)
          </legend>
          {draft.messenger.enabled ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="field">
                <span>메신저</span>
                <select
                  className="input"
                  value={draft.messenger.format}
                  onChange={(event) => set('messenger', { format: event.target.value as Draft['messenger']['format'] })}
                >
                  <option value="slack">Slack · Mattermost · Rocket.Chat</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="jandi">잔디</option>
                  <option value="dooray">두레이</option>
                  <option value="generic">일반 JSON (자체 수신기)</option>
                </select>
              </label>
              <label className="field">
                <span>웹훅 주소</span>
                <input
                  className="input"
                  value={draft.messenger.webhook_url}
                  placeholder={secretHint(view.messenger.webhook_url_set) || 'https://…'}
                  onChange={(event) => set('messenger', { webhook_url: event.target.value })}
                />
              </label>
            </div>
            <button type="button" className="btn btn-tiny" disabled={testDisabled} title={testTitle} onClick={() => test('messenger')}>
              <Send className="h-3 w-3" aria-hidden="true" /> 시험 발송
            </button>
          </>
          ) : null}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="flex items-center gap-2 text-[13.5px] font-semibold">
            <input type="checkbox" checked={draft.sms.enabled} onChange={(event) => set('sms', { enabled: event.target.checked })} />
            SMS (게이트웨이 HTTP)
          </legend>
          {draft.sms.enabled ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="field">
                <span>게이트웨이 주소</span>
                <input
                  className="input"
                  value={draft.sms.url}
                  placeholder={secretHint(view.sms.url_set) || 'https://sms.example.com/send'}
                  onChange={(event) => set('sms', { url: event.target.value })}
                />
              </label>
              <label className="field">
                <span>받는 번호 (쉼표로 구분)</span>
                <input className="input" value={draft.sms.to} placeholder="01012345678" onChange={(event) => set('sms', { to: event.target.value })} />
              </label>
            </div>
            {/* 요청 모양(방식·형식·헤더·본문 틀)은 업체에 맞출 때만 — [요청 형식] 뒤로. 적어 둔 값이 있으면 펼친다. */}
            <Disclosure
              title="요청 형식"
              summary={`${draft.sms.method} · ${draft.sms.content_type === 'json' ? 'JSON' : '폼'}${draft.sms.body_template ? ' · 본문 틀 있음' : ''}`}
            >
              <div className="space-y-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="field">
                    <span>방식</span>
                    <select className="input" value={draft.sms.method} onChange={(event) => set('sms', { method: event.target.value as 'POST' | 'GET' })}>
                      <option value="POST">POST</option>
                      <option value="GET">GET</option>
                    </select>
                  </label>
                  <label className="field">
                    <span>본문 형식</span>
                    <select className="input" value={draft.sms.content_type} onChange={(event) => set('sms', { content_type: event.target.value as 'json' | 'form' })}>
                      <option value="json">JSON</option>
                      <option value="form">폼 (x-www-form-urlencoded)</option>
                    </select>
                  </label>
                </div>
                <label className="field">
                  <span>헤더 (JSON)</span>
                  <input
                    className="input mono"
                    value={draft.sms.headers}
                    placeholder={secretHint(view.sms.headers_set) || '{"Authorization":"Bearer …"}'}
                    onChange={(event) => set('sms', { headers: event.target.value })}
                  />
                </label>
                <label className="field">
                  <span>
                    본문 틀
                    <HelpTip
                      text="업체 요청 형식에 맞춰 적습니다. {{to}} 에 받는 번호, {{message}} 에 한 줄 요약이 들어가며 번호마다 한 번씩 보냅니다."
                      label="본문 틀 도움말"
                    />
                  </span>
                  <textarea
                    className="input mono"
                    rows={3}
                    value={draft.sms.body_template}
                    placeholder={draft.sms.content_type === 'form' ? 'to={{to}}&message={{message}}' : '{"to":"{{to}}","message":"{{message}}"}'}
                    onChange={(event) => set('sms', { body_template: event.target.value })}
                  />
                </label>
              </div>
            </Disclosure>
            <button type="button" className="btn btn-tiny" disabled={testDisabled} title={testTitle} onClick={() => test('sms')}>
              <Send className="h-3 w-3" aria-hidden="true" /> 시험 발송
            </button>
          </>
          ) : null}
        </fieldset>

        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={action.pending} onClick={save}>
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
        </div>
      </div>
    </Panel>
  );
}
